/* ============================================
   CUSTOM PRODUCT GALLERY SLIDER - Dawn 15.4.0
   FINAL: Click vs Drag Intelligence + Nuclear Boundary Detection
   LINEAR NAVIGATION - No infinite loop
   ============================================ */

class CustomProductSlider {
  constructor(slider) {
    this.slider = slider;
    this.track = slider.querySelector('.product-slider-track');
    this.slides = [...slider.querySelectorAll('.product-slide')];
    this.prevButton = slider.querySelector('[data-slider-prev]');
    this.nextButton = slider.querySelector('[data-slider-next]');
    this.dotsContainer = slider.querySelector('[data-slider-dots]');
    this.dots = this.dotsContainer ? [...this.dotsContainer.querySelectorAll('[data-slider-dot]')] : [];
    
    this.currentIndex = this.slides.findIndex(slide => slide.classList.contains('is-active'));
    if (this.currentIndex === -1) this.currentIndex = 0;
    
    this.mediaGallery = slider.closest('media-gallery');
    this.sectionId = this.mediaGallery ? this.mediaGallery.id.replace('MediaGallery-', '') : null;
    
    // Touch/Drag tracking
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchEndX = 0;
    this.touchEndY = 0;
    this.isDragging = false;
    
    // Mouse drag tracking
    this.mouseStartX = 0;
    this.mouseStartY = 0;
    this.mouseEndX = 0;
    this.mouseEndY = 0;
    this.isMouseDragging = false;
    
    // Animation lock
    this.isAnimating = false;
    
    // Minimum movement threshold to distinguish click from drag
    this.dragThreshold = 15; // pixels
    
    this.init();
  }
  
  init() {
    if (this.slides.length === 0) return;
    // Safety net: if the page marked no slide as active, show the first one (otherwise every slide stays dimmed).
    if (!this.slides.some(slide => slide.classList.contains('is-active'))) {
      this.slides[this.currentIndex].classList.add('is-active');
      this.updateDots();
    }
    this.setupNavigation();
    this.setupKeyboard();
    this.setupTouchAndDrag();
    this.goToSlide(this.currentIndex, false);
    this.listenForVariantChanges();
  }
  
  setupNavigation() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!this.isAnimating && this.currentIndex > 0) {
          this.goToSlide(this.currentIndex - 1, true);
        }
      }, true);
      
      this.prevButton.addEventListener('mousedown', (e) => {
        if (this.prevButton.disabled || this.currentIndex === 0) {
          e.stopPropagation();
          e.preventDefault();
        }
      }, true);
      
      this.prevButton.addEventListener('touchstart', (e) => {
        if (this.prevButton.disabled || this.currentIndex === 0) {
          e.stopPropagation();
          e.preventDefault();
        }
      }, { passive: false, capture: true });
      
      this.prevButton.addEventListener('touchend', (e) => {
        if (this.prevButton.disabled || this.currentIndex === 0) {
          e.stopPropagation();
          e.preventDefault();
        }
      }, { passive: false, capture: true });
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!this.isAnimating && this.currentIndex < this.slides.length - 1) {
          this.goToSlide(this.currentIndex + 1, true);
        }
      }, true);
      
      this.nextButton.addEventListener('mousedown', (e) => {
        if (this.nextButton.disabled || this.currentIndex === this.slides.length - 1) {
          e.stopPropagation();
          e.preventDefault();
        }
      }, true);
      
      this.nextButton.addEventListener('touchstart', (e) => {
        if (this.nextButton.disabled || this.currentIndex === this.slides.length - 1) {
          e.stopPropagation();
          e.preventDefault();
        }
      }, { passive: false, capture: true });
      
      this.nextButton.addEventListener('touchend', (e) => {
        if (this.nextButton.disabled || this.currentIndex === this.slides.length - 1) {
          e.stopPropagation();
          e.preventDefault();
        }
      }, { passive: false, capture: true });
    }
    
    if (this.dots.length > 0) {
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', (e) => {
          e.stopPropagation();
          e.preventDefault();
          if (!this.isAnimating) {
            this.goToSlide(index, true);
          }
        });
      });
    }
  }
  
  setupKeyboard() {
    document.addEventListener('keydown', (e) => {
      const rect = this.slider.getBoundingClientRect();
      if (rect.top > window.innerHeight || rect.bottom < 0) return;
      
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (!this.isAnimating && this.currentIndex > 0) {
          this.goToSlide(this.currentIndex - 1, true);
        }
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (!this.isAnimating && this.currentIndex < this.slides.length - 1) {
          this.goToSlide(this.currentIndex + 1, true);
        }
      }
    });
  }
  
  setupTouchAndDrag() {
    // ==========================================
    // TOUCH EVENTS (Mobile)
    // ==========================================
    
    this.slider.addEventListener('touchstart', (e) => {
      // Layer 1: Element check
      if (e.target.closest('button')) return;
      
      // Layer 2: Coordinate boundary check
      const touch = e.changedTouches[0];
      const touchX = touch.clientX;
      const touchY = touch.clientY;
      
      if (this.prevButton) {
        const rect = this.prevButton.getBoundingClientRect();
        if (touchX >= rect.left && touchX <= rect.right && touchY >= rect.top && touchY <= rect.bottom) {
          return;
        }
      }
      
      if (this.nextButton) {
        const rect = this.nextButton.getBoundingClientRect();
        if (touchX >= rect.left && touchX <= rect.right && touchY >= rect.top && touchY <= rect.bottom) {
          return;
        }
      }
      
      if (this.dotsContainer) {
        const dotsRect = this.dotsContainer.getBoundingClientRect();
        if (touchX >= dotsRect.left && touchX <= dotsRect.right && touchY >= dotsRect.top && touchY <= dotsRect.bottom) {
          return;
        }
      }
      
      // Safe to start tracking
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.touchEndX = touch.clientX;
      this.touchEndY = touch.clientY;
      this.isDragging = true;
    }, { passive: true });
    
    this.slider.addEventListener('touchmove', (e) => {
      if (!this.isDragging) return;
      const touch = e.changedTouches[0];
      this.touchEndX = touch.clientX;
      this.touchEndY = touch.clientY;
    }, { passive: true });
    
    this.slider.addEventListener('touchend', () => {
      if (!this.isDragging) return;
      this.handleSwipe('touch');
      this.isDragging = false;
    });
    
    // ==========================================
    // MOUSE DRAG (Desktop)
    // ==========================================
    
    this.slider.addEventListener('mousedown', (e) => {
      // Layer 1: Element check
      if (e.target.closest('button')) return;
      
      // Layer 2: Coordinate boundary check
      const mouseX = e.clientX;
      const mouseY = e.clientY;
      
      if (this.prevButton) {
        const rect = this.prevButton.getBoundingClientRect();
        if (mouseX >= rect.left && mouseX <= rect.right && mouseY >= rect.top && mouseY <= rect.bottom) {
          return;
        }
      }
      
      if (this.nextButton) {
        const rect = this.nextButton.getBoundingClientRect();
        if (mouseX >= rect.left && mouseX <= rect.right && mouseY >= rect.top && mouseY <= rect.bottom) {
          return;
        }
      }
      
      if (this.dotsContainer) {
        const dotsRect = this.dotsContainer.getBoundingClientRect();
        if (mouseX >= dotsRect.left && mouseX <= dotsRect.right && mouseY >= dotsRect.top && mouseY <= dotsRect.bottom) {
          return;
        }
      }
      
      // Safe to start tracking
      this.mouseStartX = e.clientX;
      this.mouseStartY = e.clientY;
      this.mouseEndX = e.clientX;
      this.mouseEndY = e.clientY;
      this.isMouseDragging = true;
      this.slider.style.cursor = 'grabbing';
      e.preventDefault();
    });
    
    this.slider.addEventListener('mousemove', (e) => {
      if (!this.isMouseDragging) return;
      this.mouseEndX = e.clientX;
      this.mouseEndY = e.clientY;
    });
    
    this.slider.addEventListener('mouseup', (e) => {
      if (!this.isMouseDragging) return;
      
      this.handleSwipe('mouse');
      this.isMouseDragging = false;
      this.slider.style.cursor = 'grab';
    });
    
    this.slider.addEventListener('mouseleave', () => {
      if (this.isMouseDragging) {
        this.isMouseDragging = false;
        this.slider.style.cursor = 'grab';
      }
    });
  }
  
  handleSwipe(type) {
    let startX, endX;
    
    if (type === 'touch') {
      startX = this.touchStartX;
      endX = this.touchEndX;
    } else {
      startX = this.mouseStartX;
      endX = this.mouseEndX;
    }
    
    // Calculate horizontal distance moved
    const distanceX = Math.abs(startX - endX);
    
    // CRITICAL: If movement is less than threshold, it's a CLICK, not a DRAG
    if (distanceX < this.dragThreshold) {
      console.log('🖱️ Click detected (moved only', distanceX, 'px) - ignoring');
      return;
    }
    
    // It's a real drag/swipe - proceed with navigation
    const diff = startX - endX;
    
    console.log('👆 Drag/Swipe detected - distance:', distanceX, 'px');
    
    if (diff > 0) {
      // Dragged/swiped left - go to next (if not at end)
      if (!this.isAnimating && this.currentIndex < this.slides.length - 1) {
        console.log('→ Moving to next slide');
        this.goToSlide(this.currentIndex + 1, true);
      }
    } else {
      // Dragged/swiped right - go to previous (if not at start)
      if (!this.isAnimating && this.currentIndex > 0) {
        console.log('← Moving to previous slide');
        this.goToSlide(this.currentIndex - 1, true);
      }
    }
    
    // Reset
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.mouseStartX = 0;
    this.mouseEndX = 0;
  }
  
  goToSlide(index, userInitiated = false) {
    if (index < 0 || index >= this.slides.length) return;
    if (index === this.currentIndex && !userInitiated) return;
    
    this.isAnimating = true;
    this.currentIndex = index;
    
    const offset = index * -100;
    this.track.style.transform = `translateX(${offset}%)`;
    
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index);
    });
    
    this.updateDots();
    this.updateArrows();
    
    if (userInitiated) {
      this.playActiveMedia();
      this.notifyDawn();
    }
    
    this.slider.dispatchEvent(new CustomEvent('slideChanged', {
      bubbles: true,
      detail: { currentElement: this.slides[index], currentIndex: index }
    }));
    
    setTimeout(() => { this.isAnimating = false; }, 450);
  }
  
  updateDots() {
    if (this.dots.length === 0) return;
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.currentIndex);
      dot.setAttribute('aria-current', i === this.currentIndex ? 'true' : 'false');
    });
  }
  
  updateArrows() {
    if (this.prevButton) {
      const isAtStart = this.currentIndex === 0;
      this.prevButton.disabled = isAtStart;
      this.prevButton.setAttribute('aria-disabled', isAtStart ? 'true' : 'false');
      this.prevButton.style.opacity = isAtStart ? '0.3' : '1';
      this.prevButton.style.cursor = isAtStart ? 'not-allowed' : 'pointer';
    }
    
    if (this.nextButton) {
      const isAtEnd = this.currentIndex === this.slides.length - 1;
      this.nextButton.disabled = isAtEnd;
      this.nextButton.setAttribute('aria-disabled', isAtEnd ? 'true' : 'false');
      this.nextButton.style.opacity = isAtEnd ? '0.3' : '1';
      this.nextButton.style.cursor = isAtEnd ? 'not-allowed' : 'pointer';
    }
  }
  
  listenForVariantChanges() {
    if (!this.mediaGallery || !this.sectionId) return;
    
    document.addEventListener('variant:change', (event) => {
      const variantMedia = event.detail?.variant?.featured_media;
      if (!variantMedia) return;
      
      const mediaId = `${this.sectionId}-${variantMedia.id}`;
      const slideIndex = this.slides.findIndex(slide => slide.dataset.mediaId === mediaId);
      
      if (slideIndex !== -1 && slideIndex !== this.currentIndex) {
        this.goToSlide(slideIndex, false);
      }
    });
  }
  
  playActiveMedia() {
    if (window.pauseAllMedia) window.pauseAllMedia();
    
    const activeSlide = this.slides[this.currentIndex];
    if (!activeSlide) return;
    
    const deferredMedia = activeSlide.querySelector('.deferred-media');
    if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
      deferredMedia.loadContent(false);
    }
  }
  
  notifyDawn() {
    if (!this.mediaGallery) return;
    
    const activeSlide = this.slides[this.currentIndex];
    if (!activeSlide) return;
    
    const mediaId = activeSlide.dataset.mediaId;
    if (!mediaId) return;
    
    if (typeof this.mediaGallery.setActiveMedia === 'function') {
      this.mediaGallery.setActiveMedia(mediaId, false);
    }
  }
  
  goToMediaId(mediaId) {
    const slideIndex = this.slides.findIndex(slide => slide.dataset.mediaId === mediaId);
    if (slideIndex !== -1) {
      this.goToSlide(slideIndex, false);
      return true;
    }
    return false;
  }
  
  getCurrentSlide() {
    return {
      index: this.currentIndex,
      slide: this.slides[this.currentIndex],
      mediaId: this.slides[this.currentIndex]?.dataset.mediaId,
      mediaType: this.slides[this.currentIndex]?.dataset.mediaType
    };
  }
  
  refresh() {
    this.slides = [...this.slider.querySelectorAll('.product-slide')];
    this.dots = this.dotsContainer ? [...this.dotsContainer.querySelectorAll('[data-slider-dot]')] : [];
    if (this.currentIndex >= this.slides.length) {
      this.currentIndex = this.slides.length - 1;
    }
    this.goToSlide(this.currentIndex, false);
  }
}

function initCustomSliders() {
  const sliders = document.querySelectorAll('[data-product-slider]');
  if (sliders.length === 0) return;
  
  sliders.forEach((slider) => {
    try {
      const instance = new CustomProductSlider(slider);
      slider.customSlider = instance;
    } catch (error) {
      console.error('[Custom Slider] Error:', error);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustomSliders);
} else {
  initCustomSliders();
}

document.addEventListener('shopify:section:load', initCustomSliders);

window.CustomProductSlider = CustomProductSlider;
window.initCustomSliders = initCustomSliders;